/* eslint-disable max-lines */
import { useArtwork, useArtworkForm } from "@design-lab/entities/artwork";
import {
  currentPinningSessionAtom,
  pinningModeAtom,
  pinningSessionsAtom,
  useCancelPinningSession
} from "@design-lab/entities/pinning";
import {
  trackDesignLabPinningInstructionsRemoved,
  trackDesignLabPinningModeEntered,
  trackDesignLabPinningSessionSummaryEdited,
  trackPinsConfirmationAddToCartClicked,
  trackPinsConfirmationDeleteClicked,
  trackPinsConfirmationEditClicked,
  trackPinsConfirmationModalClosed,
  trackPinsConfirmationModalShown
} from "@design-lab/shared/lib/analytics";
import { useExperiments } from "@design-lab/shared/lib/config";
import { shouldReorderDesignLabTabs } from "@design-lab/shared/lib/experiments";
import { designSubTabAtom } from "@design-lab/shared/lib/tab-navigation";
import type { ArtworkForm, Side } from "@design-lab/shared/types";
import { atom, useAtom, useAtomValue, useSetAtom } from "jotai";
import { useCallback, useMemo, useState } from "react";
import { useWatch } from "react-hook-form";
import { useSearchParams } from "react-router";

export const reviewPinningModalOpenAtom = atom<boolean>(false);

// Atom to store the add-to-cart callback
const addToCartCallbackAtom = atom<(() => void | Promise<void>) | null>(null);

export type CompletedSessionInfo = {
  artworkId: string;
  sessionId: string;
  summary: string | null;
  side: "front" | "back";
  artworkImageUrl: string | undefined;
};

/**
 * Get the enhanced image URL based on user settings (removeBackground, enhancementTier)
 */
function getEnhancedArtworkUrl(
  artwork: ReturnType<ReturnType<typeof useArtwork>["getArtwork"]>,
  info: ArtworkForm["info"]["front"] | ArtworkForm["info"]["back"] | undefined
): string | undefined {
  if (!artwork) return undefined;

  const removeBackground = info?.removeBackground ?? true;
  const enhancementTier = info?.enhancementTier ?? "full_enhancements";
  const editImageManual = info?.editImageManual ?? false;
  const canRemoveBackground = artwork.canRemoveBg ?? true;

  // If using manual edit and it's available
  if (editImageManual && artwork.manualEditUrl) {
    return artwork.manualEditUrl;
  }

  // For print_original tier, use only originalImageUrl to match backend behavior (artwork.original_image)
  // This ensures pin coordinates calculated from displayed image match backend image dimensions
  if (enhancementTier === "print_original") {
    return artwork.originalImageUrl;
  }

  // For full_enhancements, respect removeBackground setting
  if (removeBackground && canRemoveBackground) {
    return (
      artwork.noBgUrl || artwork.noBgNoTrimUrl || artwork.withBgUrl || artwork.originalImageUrl
    );
  }

  // Default: use withBgUrl or original
  return artwork.withBgUrl || artwork.originalImageUrl;
}

export function useReviewPinningModal() {
  const { form } = useArtworkForm();
  const { getArtwork } = useArtwork();
  const [isModalOpen, setIsModalOpen] = useAtom(reviewPinningModalOpenAtom);
  const [addToCartCallback, setAddToCartCallback] = useAtom(addToCartCallbackAtom);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const pinningSessions = useAtomValue(pinningSessionsAtom);
  const setPinningSessions = useSetAtom(pinningSessionsAtom);
  const setCurrentPinningSession = useSetAtom(currentPinningSessionAtom);
  const setPinningMode = useSetAtom(pinningModeAtom);
  const setDesignSubTab = useSetAtom(designSubTabAtom);
  const { mutate: cancelSession } = useCancelPinningSession();
  const [searchParams, setSearchParams] = useSearchParams();
  const experiments = useExperiments();
  const showSubTabs = shouldReorderDesignLabTabs(experiments?.abDesignLabReorderTabs);

  const artworkForm = useWatch({
    control: form.control
  }) as ArtworkForm | undefined;

  // Get artwork IDs from the form
  const frontArtworkId =
    artworkForm?.front?.artworkId || artworkForm?.frontLeftChest?.artworkId || "";
  const backArtworkId = artworkForm?.back?.artworkId || "";

  // Get completed sessions for each side
  const completedSessions = useMemo(() => {
    const sessions: CompletedSessionInfo[] = [];

    if (frontArtworkId) {
      const session = pinningSessions[frontArtworkId];
      if (session?.state === "completed") {
        const artwork = getArtwork(frontArtworkId);
        const frontInfo = artworkForm?.info?.front;
        sessions.push({
          artworkId: frontArtworkId,
          sessionId: session.sessionId,
          summary: session.summary,
          side: "front",
          artworkImageUrl: getEnhancedArtworkUrl(artwork, frontInfo)
        });
      }
    }

    if (backArtworkId) {
      const session = pinningSessions[backArtworkId];
      if (session?.state === "completed") {
        const artwork = getArtwork(backArtworkId);
        const backInfo = artworkForm?.info?.back;
        sessions.push({
          artworkId: backArtworkId,
          sessionId: session.sessionId,
          summary: session.summary,
          side: "back",
          artworkImageUrl: getEnhancedArtworkUrl(artwork, backInfo)
        });
      }
    }

    return sessions;
  }, [frontArtworkId, backArtworkId, pinningSessions, getArtwork, artworkForm?.info]);

  const hasCompletedSessions = completedSessions.length > 0;

  const openModal = useCallback(
    (onConfirmAddToCart?: () => void | Promise<void>) => {
      setAddToCartCallback(() => onConfirmAddToCart || null);
      setIsModalOpen(true);
      trackPinsConfirmationModalShown({
        ...(frontArtworkId ? { frontArtworkId } : {}),
        ...(backArtworkId ? { backArtworkId } : {})
      });
    },
    [setIsModalOpen, setAddToCartCallback, frontArtworkId, backArtworkId]
  );

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setAddToCartCallback(null);
    trackPinsConfirmationModalClosed();
  }, [setIsModalOpen, setAddToCartCallback]);

  const handleEdit = useCallback(
    (session: CompletedSessionInfo) => {
      // Track modal-specific event
      trackPinsConfirmationEditClicked({
        artworkId: session.artworkId,
        side: session.side
      });

      // Track session summary edit clicked
      trackDesignLabPinningSessionSummaryEdited({
        artworkId: session.artworkId,
        sessionId: session.sessionId
      });

      // Track pinning mode entered (same as PrintPerfect.tsx)
      trackDesignLabPinningModeEntered({
        artworkId: session.artworkId,
        sessionId: session.sessionId,
        hasExistingPins: true
      });

      // Close modal first
      setIsModalOpen(false);
      setAddToCartCallback(null);

      // Switch to the correct side/placement for this session
      const targetPlacement =
        session.side === "back" ? "back" : artworkForm?.selectedFrontPlacement || "front";
      form.setValue("currentPlacement", targetPlacement);

      // Update both URL tab and sub-tab atom to ensure correct side is shown
      setDesignSubTab(session.side);
      const next = new URLSearchParams(searchParams?.toString() || "");
      // Use "design" tab when A/B variant is active, otherwise use session.side
      next.set("tab", showSubTabs ? "design" : session.side);
      setSearchParams(next, { replace: true });

      // Wait for artboard to re-render with correct side before entering pinning mode
      setTimeout(() => {
        setCurrentPinningSession({
          artworkId: session.artworkId,
          sessionId: session.sessionId
        });
        setPinningMode(true);
      }, 150);
    },
    [
      setCurrentPinningSession,
      setPinningMode,
      setIsModalOpen,
      setAddToCartCallback,
      artworkForm,
      form,
      setDesignSubTab,
      searchParams,
      setSearchParams,
      showSubTabs
    ]
  );

  const handleDelete = useCallback(
    (session: CompletedSessionInfo) => {
      // Track modal-specific event
      trackPinsConfirmationDeleteClicked({
        artworkId: session.artworkId,
        side: session.side
      });

      // Call API to delete the session, then update state on success
      cancelSession(session.sessionId, {
        onSuccess: () => {
          // Track instructions removed
          trackDesignLabPinningInstructionsRemoved({
            artworkId: session.artworkId,
            sessionId: session.sessionId
          });

          // Remove session from state
          setPinningSessions(prev => {
            const next = { ...prev };
            delete next[session.artworkId];
            return next;
          });

          // Clear jiffyDesignServices and enhancementRequestNote for the side
          const side: Side = session.side === "back" ? "back" : "front";
          form.setValue(`info.${side}.jiffyDesignServices`, false);
          form.setValue(`info.${side}.enhancementRequestNote`, "");
        }
      });
    },
    [cancelSession, setPinningSessions, form]
  );

  const handleAddToCart = useCallback(async () => {
    trackPinsConfirmationAddToCartClicked();

    // Call the stored callback to proceed with add to cart
    if (!addToCartCallback) {
      setIsModalOpen(false);
      return;
    }
    const callback = addToCartCallback;
    setAddToCartCallback(null);
    setIsAddingToCart(true);
    try {
      await callback();
    } finally {
      setIsAddingToCart(false);
      setIsModalOpen(false);
    }
  }, [addToCartCallback, setIsModalOpen, setAddToCartCallback]);

  return {
    isModalOpen,
    isAddingToCart,
    completedSessions,
    hasCompletedSessions,
    openModal,
    closeModal,
    handleEdit,
    handleDelete,
    handleAddToCart
  };
}
